import React, { useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { Typography, TextField, Button, Box, LinearProgress, Alert } from '@mui/material';

// Reusable "paste a URL, or upload a file instead" field for the two
// conference-configurable images (t-shirt photo, website banner). Uploads go
// to Firebase Storage under conferenceImages/{storagePath} - see
// storage.rules for who can write there. The URL text field is left
// editable so an existing external image link still works without needing
// to be re-uploaded.
export default function ImageUploadField({ label, helperText, value, onChange, disabled, storagePath }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  function handlePickFile() {
    fileInputRef.current?.click();
  }

  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    setError(null);
    setUploading(true);
    setProgress(0);

    const path = `conferenceImages/${storagePath}-${Date.now()}-${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      (snapshot) => setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      (err) => {
        console.error('Image upload failed:', err);
        setError(err.message);
        setUploading(false);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          onChange(url);
        } catch (err) {
          console.error('Failed to get uploaded image URL:', err);
          setError(err.message);
        } finally {
          setUploading(false);
        }
      },
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth label={label} helperText={helperText}
          value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        />
        <Button
          variant="outlined" onClick={handlePickFile} disabled={disabled || uploading}
          sx={{ mt: '2px', flexShrink: 0 }}
        >
          Upload...
        </Button>
        <input
          ref={fileInputRef} type="file" accept="image/*" hidden
          onChange={handleFileSelected}
        />
      </Box>
      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">Uploading... {progress}%</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {value && !uploading && (
        <Box
          component="img" src={value} alt={`${label} preview`}
          sx={{ mt: 1, maxWidth: 200, maxHeight: 120, objectFit: 'contain', display: 'block', border: '1px solid #ddd', borderRadius: 1 }}
        />
      )}
    </Box>
  );
}
